import type { Channel, FavouriteChannels, PrivateChannelOrders, RouterAppContext, UserSession, Workspace } from "@/utils/custom-types";
import { createContext, useContext, type ReactNode } from "react";
import { Route as WorkspaceRoute } from "@/routes/$workspaceId/route";
import { useQueries, useQuery } from "@tanstack/react-query";
import { trpc } from "@/utils/trpc";
import type { WorkspaceTabs } from "./tabs-context";
import type { SplitLayout } from "./split-panel-context";
import type { WorkspaceMemberWithNameAndEmail } from "server/workspace-types";

export type WorkspaceSection =
	| "home"
	| "chats"
	| "docs"
	| "sheets"
	| "boards"
	| "meets"
	| "storage"
	| "passwords"
	| "automations"
	| "projects"
	| "more";

interface WorkspaceContextType {
	userWorkspaces: Workspace[];
    channels: Channel[];
    tabState: WorkspaceTabs;
    panelState: SplitLayout;
    sidebarState: Record<string, boolean>;
	activeSection: WorkspaceSection;
	workspaceMembers: WorkspaceMemberWithNameAndEmail[];
	favouriteChannels: FavouriteChannels;
	privateChannelOrders: PrivateChannelOrders;
	channelsLoading: boolean;
	tabStateLoading: boolean;
	panelStateLoading: boolean;
	sidebarStateLoading: boolean;
	activeSectionLoading: boolean;
	workspaceMembersLoading: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export function WorkspaceProvider({
	children,
}: {
	children: ReactNode;
}) {
    const { currentWorkspace, session } =
        WorkspaceRoute.useLoaderData();

	const [workspaces, channels, tabState, panelState, sidebarState, activeSection, workspaceMembers] = useQueries({
		queries: [
			trpc.workspace.getUserWorkspaces.queryOptions(),
			trpc.channel.getByUserAndWorkspace.queryOptions({
				workspaceId: Number(currentWorkspace.id),
			}),
			trpc.me.getTabState.queryOptions({
				workspaceId: Number(currentWorkspace.id),
			}),
			trpc.me.getPanelState.queryOptions({
				workspaceId: Number(currentWorkspace.id),
			}),
			trpc.me.getSidebarState.queryOptions({
				workspaceId: Number(currentWorkspace.id),
			}),
			trpc.me.getActiveSection.queryOptions({
				workspaceId: Number(currentWorkspace.id),
			}),
			trpc.workspace.getMembers.queryOptions({
				workspaceId: Number(currentWorkspace.id),
			}),
		],
	});

	// console.log(`[workspace context get panel state] for workspace ${currentWorkspace.id} (${currentWorkspace.name}): `, JSON.stringify(panelState.data?.panelState, null, 2));

	return (
		<WorkspaceContext.Provider
			value={{
				userWorkspaces: (workspaces.data ?? session.user.userWorkspaces) as Workspace[],
				channels: (channels.data ?? []) as Channel[],
				tabState: (tabState.data?.tabState ?? {}) as WorkspaceTabs,
				panelState: (panelState.data?.panelState ?? {}) as SplitLayout,
				sidebarState: sidebarState.data?.sidebarState ?? {},
				activeSection: activeSection.data?.activeSection ?? "home",
				workspaceMembers: workspaceMembers.data ?? [],
				favouriteChannels: sidebarState.data?.favouriteChannels ?? {},
				privateChannelOrders: sidebarState.data?.privateChannelOrders ?? {},
				channelsLoading: channels.isLoading && !channels.data,
				tabStateLoading: tabState.isLoading,
				panelStateLoading: panelState.isLoading,
				sidebarStateLoading: sidebarState.isLoading && !sidebarState.data,
				activeSectionLoading: activeSection.isLoading && !activeSection.data,
				workspaceMembersLoading: workspaceMembers.isLoading && !workspaceMembers.data,
			}}
		>
			{children}
		</WorkspaceContext.Provider>
	);
}

export function useWorkspaceContext() {
	const context = useContext(WorkspaceContext);
	if (context === undefined) {
		throw new Error(
			"useWorkspaceContext must be used within a WorkspaceProvider",
		);
	}
	return context;
} 